import * as React from "react";
import { FaEdit, FaLock, FaPlus, FaTrash } from "react-icons/fa";
import styled, { css } from "styled-components";

import { AddButton } from "../../../App/Common/Components/FormComponents";
import { ICourse, IScormPackage } from "../../../App/types/common";
import { PageContextProvider } from "../../../context/numbered-pagination";
import { fmtDateToLocalZone, pluralize } from "../../../utils";
import { Header as H1 } from "../../utils/Headings";
import * as Helper from "../../utils/helpers";
import { usePagination } from "../../utils/Pagination/hooks";
import { ActionRow, BottomPagination, Count, TopPagination } from "../../utils/Pagination/NumberedPagination/common";
import { ITableProps, Table } from "../../utils/Pagination/NumberedPagination/table";
import ActionConfirmationButton from "../CourseAuthor/ActionConfirmation";
import { Link as CourseLink } from "../utils";

export interface ICoursesProps {
  courses: ICourse[];
  scormPackages: IScormPackage[];
  createCourse: () => void;
  deleteCourse: (courseId: string) => () => void;
}

const CoursesContainer = styled.div(
  ({ theme }) => `
  width: 100%;

  font-family: ${theme.primaryTheme.primaryFontFamily};
  font-size: ${theme.primaryTheme.primaryFontSize};
`
);

const CourseActionRow = styled(ActionRow)`
  align-items: center;
  width: 100%;
  margin: 2rem 0;

  @media (${({ theme }) => theme.breakpoints.min800}) {
    flex-direction: row;
  }
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  &:hover {
    background-color: rgba(225, 225, 225, 1);
  }
`;

const CourseName = styled.div`
  margin: 0 1rem 0 0;
`;

const StyledPlus = styled(FaPlus)`
  margin-right: 0.5rem;
`;

const CustomAddButton = styled(AddButton)`
  width: 16rem;
`;

export const CourseRow: React.SFC<{
  course: ICourse;
  delete: () => void;
}> = props => (
  <Row>
    <CourseLink to={`/course-author/${props.course.courseId}`}>
      <CourseName>{props.course.displayName}</CourseName>
    </CourseLink>
    <ActionConfirmationButton action="Delete" actionFn={props.delete} ActionIcon={FaTrash} />
  </Row>
);

const centeredText = css`
  text-align: center;
`;

interface ITableCourse {
  actions: React.ReactNode;
  dateAdded: string;
  dateLastPublished: string;
  lastEditedBy: string;
  scormPackagesCount: number;
  title: React.ReactNode;
}

const CourseTableContainer = styled.div`
  overflow-x: auto;
`;

const CourseTable: React.FC<ITableProps<ITableCourse>> = ({ columns, data }) => {
  return (
    <CourseTableContainer>
      <Table columns={columns} data={data} />
    </CourseTableContainer>
  );
};

const Courses: React.FC<ICoursesProps> = props => {
  const scormPackagesByCourse = Helper.scormPackagesByCourse(props.scormPackages);
  const toTableCourse = ({ courseId, displayName, lastEditorName, locked, createdAt, publicationInfo }: ICourse) => ({
    actions: <ActionConfirmationButton action="Delete" actionFn={props.deleteCourse(courseId)} ActionIcon={FaTrash} />,
    dateAdded: fmtDateToLocalZone(createdAt),
    dateLastPublished: publicationInfo ? fmtDateToLocalZone(publicationInfo.createdAt) : "-",
    lastEditedBy: lastEditorName,
    scormPackagesCount: scormPackagesByCourse[courseId] ? scormPackagesByCourse[courseId].length : 0,
    title: (
      <CourseLink to={`/course-author/${courseId}`}>
        <CourseName>
          {displayName}
          {locked && <FaLock style={{ margin: "0 0 0 0.5rem" }} />}
        </CourseName>
        <FaEdit style={{ minWidth: "1em", alignSelf: "center" }} />
      </CourseLink>
    ),
  });
  const total = props.courses.length;
  const { value, pagedData } = usePagination(props.courses);

  return (
    <CoursesContainer>
      <PageContextProvider value={value}>
        <H1>Courses</H1>
        <CourseActionRow>
          <CustomAddButton onClick={props.createCourse}>
            <StyledPlus />
            Create New Course
          </CustomAddButton>
          <TopPagination total={total} />
          <Count>{pluralize("Course", props.courses.length)}</Count>
        </CourseActionRow>
        <CourseTable
          columns={[
            { name: "Course Title", accessor: "title" },
            { name: "Date Created", accessor: "dateAdded" },
            { name: "Last Published", accessor: "dateLastPublished" },
            {
              accessor: "scormPackagesCount",
              name: "No. of SCORM Packages",
              styles: centeredText,
            },
            { name: "Last Edited By", accessor: "lastEditedBy" },
            { name: "Actions", accessor: "actions" },
          ]}
          data={pagedData.map(toTableCourse)}
        />
        <BottomPagination total={total} />
      </PageContextProvider>
    </CoursesContainer>
  );
};

export default Courses;
